// Clientes: resumen de compras por cliente y filtros, traídos del prototipo (Clientes y Campañas, smoke05).
import { emailOk, sinTildes } from './texto';
import { addDias } from './fechas';
import { cuenta, totalesPedido, type Cliente, type Pedido } from './pedidos';

// Compras de antes del sistema (importadas de los Excel de años anteriores), en pesos.
export interface CompraHistorica { cliente_id: string; fecha: string; producto: string; cantidad: number; valor: number }
export interface ResumenCliente {
  c: Cliente; compras: number; pedidos: number; historicas: number; total: number; saldo: number; ultimo: string; primero: string; cotAbiertas: number;
  skus: Map<string, number>; otros: Map<string, number>; canales: Set<number>; colores: Set<string>; favorito: string;
}

// Solo cuentan los pedidos que son venta (confirmados y enviados). Total y saldo en pesos, con la tasa de cada pedido.
// Las compras anteriores suman como una compra por día (un Excel de ventas suele tener una fila por prenda) y su
// producto cuenta como del catálogo si el nombre coincide (skuDe); si no, queda como texto en "otros".
export function resumenClientes(clientes: Cliente[], pedidos: Pedido[], historicas: CompraHistorica[] = [], skuDe: (producto: string) => string | null = () => null): Map<string, ResumenCliente> {
  const r = new Map(clientes.map(c => [c.id, { c, compras: 0, pedidos: 0, historicas: 0, total: 0, saldo: 0, ultimo: '', primero: '', cotAbiertas: 0, skus: new Map<string, number>(), otros: new Map<string, number>(), canales: new Set<number>(), colores: new Set<string>(), favorito: '' }]));
  const dias = new Map<string, Set<string>>();
  for (const h of historicas) {
    const x = r.get(h.cliente_id);
    if (!x) continue;
    if (!dias.has(h.cliente_id)) dias.set(h.cliente_id, new Set());
    dias.get(h.cliente_id)!.add(h.fecha);
    x.total += +h.valor || 0;
    if (h.fecha > x.ultimo) x.ultimo = h.fecha;
    if (!x.primero || h.fecha < x.primero) x.primero = h.fecha;
    const sku = h.producto ? skuDe(h.producto) : null, n = +h.cantidad || 1;
    if (sku) x.skus.set(sku, (x.skus.get(sku) || 0) + n); else if (h.producto) x.otros.set(h.producto, (x.otros.get(h.producto) || 0) + n);
  }
  for (const [id, d] of dias) { const x = r.get(id)!; x.historicas = d.size; x.compras = d.size; }
  for (const p of pedidos) {
    const x = r.get(p.cliente_id);
    if (!x) continue;
    if (p.estado === 'cotizacion') x.cotAbiertas++;
    if (!cuenta(p)) continue;
    const t = totalesPedido(p);
    x.pedidos++; x.compras++; x.total += t.total * t.tasa; x.saldo += t.saldo * t.tasa;
    if (p.fecha > x.ultimo) x.ultimo = p.fecha;
    if (!x.primero || p.fecha < x.primero) x.primero = p.fecha;
    x.canales.add(p.canal_id);
    for (const l of p.lineas) { x.skus.set(l.sku, (x.skus.get(l.sku) || 0) + (+l.cantidad || 0)); if (l.color) x.colores.add(l.color); }
  }
  for (const x of r.values()) x.favorito = [...x.skus, ...x.otros].sort((a, b) => b[1] - a[1])[0]?.[0] || '';
  return r;
}

export const diasEntre = (a: string, b: string) => Math.round((new Date(b + 'T12:00:00').getTime() - new Date(a + 'T12:00:00').getTime()) / 864e5);

export type Compras = 'todos' | 'con' | 'sin' | 'una' | 'recurrentes';
export type Ultima = 'todas' | '30' | '90' | 'mas90' | 'mas180' | 'mas365';
export interface FiltroClientes { paises: string[]; compras: Compras; ultima: Ultima; productos: string[]; canales: number[]; minTotal: number; conEmail: boolean; acepta: boolean; cotAbierta: boolean }
export const FILTRO_VACIO: FiltroClientes = { paises: [], compras: 'todos', ultima: 'todas', productos: [], canales: [], minTotal: 0, conEmail: false, acepta: false, cotAbierta: false };
export const OP_COMPRAS: { id: Compras; label: string }[] = [{ id: 'todos', label: 'Todos' }, { id: 'con', label: 'Con compras' }, { id: 'sin', label: 'Sin compras' }, { id: 'una', label: 'Una compra' }, { id: 'recurrentes', label: 'Recurrentes (2+)' }];
export const OP_ULTIMA: { id: Ultima; label: string }[] = [{ id: 'todas', label: 'Cualquier fecha' }, { id: '30', label: 'En los últimos 30 días' }, { id: '90', label: 'En los últimos 90 días' },
  { id: 'mas90', label: 'Hace más de 90 días' }, { id: 'mas180', label: 'Hace más de 6 meses' }, { id: 'mas365', label: 'Hace más de un año' }];
export const nFiltros = (f: FiltroClientes) => [f.paises.length, f.compras !== 'todos', f.ultima !== 'todas', f.productos.length, f.canales.length, f.minTotal > 0, f.conEmail, f.acepta, f.cotAbierta].filter(Boolean).length;

// Búsqueda (nombre, correo o 3+ dígitos del WhatsApp) y filtros. hoy en ISO para poder probar con fechas fijas.
export function pasaFiltro(x: ResumenCliente, f: FiltroClientes, busqueda: string, hoy: string): boolean {
  const k = x.c, qq = sinTildes(busqueda).trim(), qd = busqueda.replace(/\D/g, '');
  if (qq && !(sinTildes(k.nombre).includes(qq) || (qd.length >= 3 && k.whatsapp.includes(qd)) || sinTildes(k.email || '').includes(qq))) return false;
  if (f.paises.length && !f.paises.includes(k.pais)) return false;
  if (f.compras === 'con' && !x.compras) return false;
  if (f.compras === 'sin' && x.compras) return false;
  if (f.compras === 'una' && x.compras !== 1) return false;
  if (f.compras === 'recurrentes' && x.compras < 2) return false;
  if (f.ultima !== 'todas') {
    if (!x.ultimo) return false;
    const d = diasEntre(x.ultimo, hoy), n = +f.ultima.replace('mas', '');
    if (f.ultima.startsWith('mas') ? d <= n : d > n) return false;
  }
  if (f.productos.length && !f.productos.some(s => x.skus.has(s))) return false;
  if (f.canales.length && !f.canales.some(id => x.canales.has(id))) return false;
  if (f.minTotal > 0 && x.total < f.minTotal) return false;
  if (f.conEmail && !emailOk(k.email || '')) return false;
  if (f.acepta && !k.acepta_mensajes) return false;
  if (f.cotAbierta && !x.cotAbiertas) return false;
  return true;
}
export const haceDias = (iso: string, n: number) => addDias(iso, -n);

// Producto del catálogo para un texto de los Excel anteriores: por nombre exacto (sin tildes ni mayúsculas) o por SKU.
export function buscadorSku(productos: { sku: string; nombre: string }[]): (texto: string) => string | null {
  const m = new Map<string, string>();
  for (const p of productos) { m.set(sinTildes(p.nombre).trim(), p.sku); m.set(sinTildes(p.sku).trim(), p.sku); }
  return t => m.get(sinTildes(t).replace(/\s+/g, ' ').trim()) || null;
}

// Orden de la lista de clientes: se elige con el encabezado de cada columna. Por defecto, quien más ha comprado primero.
export type ColOrden = 'nombre' | 'whatsapp' | 'compras' | 'total' | 'ultima' | 'mensajes';
export interface Orden { col: ColOrden; dir: 'asc' | 'desc' }
export const ORDEN_INICIAL: Orden = { col: 'total', dir: 'desc' };
// Al tocar una columna por primera vez: texto de la A a la Z; cifras, fechas y autorización, de mayor a menor.
export const DIR_INICIAL: Record<ColOrden, Orden['dir']> = { nombre: 'asc', whatsapp: 'asc', compras: 'desc', total: 'desc', ultima: 'desc', mensajes: 'desc' };
export const ETIQUETA_ORDEN: Record<ColOrden, string> = { nombre: 'Cliente', whatsapp: 'WhatsApp', compras: 'Compras', total: 'Compra total', ultima: 'Última compra', mensajes: 'Mensajes' };
export function ordenarClientes(lista: ResumenCliente[], { col, dir }: Orden): ResumenCliente[] {
  const sg = dir === 'asc' ? 1 : -1, es = (a: string, b: string) => a.localeCompare(b, 'es');
  const por = (a: ResumenCliente, b: ResumenCliente): number => {
    switch (col) {
      case 'nombre': return es(a.c.nombre, b.c.nombre);
      case 'whatsapp': return es(a.c.pais, b.c.pais) || es(a.c.whatsapp, b.c.whatsapp);
      case 'compras': return a.compras - b.compras;
      case 'total': return a.total - b.total;
      case 'ultima': return es(a.ultimo, b.ultimo);
      case 'mensajes': return +a.c.acepta_mensajes - +b.c.acepta_mensajes;
    }
  };
  return [...lista].sort((a, b) => {
    if (col === 'ultima' && (!a.ultimo || !b.ultimo) && a.ultimo !== b.ultimo) return a.ultimo ? -1 : 1;   // sin compras, siempre al final
    return sg * por(a, b) || b.total - a.total || es(a.c.nombre, b.c.nombre);
  });
}

// Los clientes archivados ("borrados" con pedidos) no salen en la lista, las exportaciones ni las campañas; sus pedidos sí los conservan.
export const activos = (cs: Cliente[]) => cs.filter(c => !c.archivado_en);

// Lo que se le dice a quien va a borrar un cliente. Con pedidos se sabe que se archiva; sin pedidos, solo la base sabe si estuvo en una campaña.
export function textoBorrado(nombre: string, pedidos: number): string {
  return pedidos > 0
    ? `${nombre} tiene ${pedidos} ${pedidos === 1 ? 'pedido' : 'pedidos'}. Saldrá de la lista de clientes, pero ${pedidos === 1 ? 'su pedido se queda' : 'sus pedidos se quedan'} tal cual, con su nombre y teléfono. Si vuelve a comprar, reaparece.`
    : `${nombre} no tiene pedidos. Se borra para siempre, con sus datos y compras de antes del sistema. Si estuvo en una campaña de mensajes, solo se archiva.`;
}
